import { platformModifierKey } from "ol/events/condition";
import { boundingExtent } from "ol/extent";
import type { Extent } from "ol/extent";
import type { FeatureLike } from "ol/Feature";
import Feature from "ol/Feature";
import PointGeom from "ol/geom/Point";
import DragBoxInteraction from "ol/interaction/DragBox";
import VectorLayer from "ol/layer/Vector";
import OlMap from "ol/Map";
import Overlay from "ol/Overlay";
import { fromLonLat, transformExtent } from "ol/proj";
import VectorSource from "ol/source/Vector";
import View from "ol/View";

import type { Site } from "../../../api/site/structs";
import { calcSiteStyle } from "../../../common/calcstyle";
import { DragMessageControl } from "../../../common/dragmessagecontrol";
import { StationLegendControl } from "../../../common/stationlegendcontrol";
import { constants } from "../../../constants";
import { obsNetworkIdToString } from "../../../enums";
import type { ObsNetworkId } from "../../../enums";
import { pageUrls } from "../../../urls";
import { orderBy } from "../../../utils/arrayutil/orderby";
import hx from "../../../utils/hydroxie";
import { InheritableEventEmitter } from "../../../utils/inheritableeventemitter";
import { gsiBlank, gsiPale, gsiPhotoAndPale, gsiStd, gsiStdEnglish } from "../../../utils/maputil/createbaselayer";
import { createControls } from "../../../utils/maputil/createcontrols";
import { createInteractions } from "../../../utils/maputil/createinteractions";
import { findNearestFeature } from "../../../utils/maputil/findnearestfeature";
import LayerSwitcher from "../../../utils/maputil/ollayerswitchermod";

/**
 * 観測点検索→地図エリア
 */
export class MapArea
    extends InheritableEventEmitter<{ selectExtent: [extent: Extent] }> {

    /** 地図エリアのルート要素 */
    #elem: HTMLElement;
    /** 観測点データのベクターソース */
    #source: VectorSource<Feature<PointGeom>>;

    /**
     * コンストラクター。
     *
     * @param elem 地図エリアのルート要素
     */
    constructor(elem: HTMLElement) {
        super();

        this.#elem = elem;
        this.#source = new VectorSource({});
    }

    /**
     * 初期化処理を行う。
     * OpenLayers地図を構築し、クリックイベントやドラッグボックスインタラクションを設定する。
     */
    async init() {
        const controls = createControls();
        controls.push(new LayerSwitcher());
        controls.push(new StationLegendControl());
        controls.push(new DragMessageControl());

        const interactions = createInteractions();
        const drag = new DragBoxInteraction({ condition: platformModifierKey });
        drag.on("boxend", () => {
            const extent3857 = drag.getGeometry().getExtent();
            const extent4326 = transformExtent(extent3857, "EPSG:3857", "EPSG:4326");
            this.emit("selectExtent", extent4326);
        });
        interactions.push(drag);

        const sourcesLayer = new VectorLayer({
            source: this.#source,
            style: (f: FeatureLike) => {
                const obsNetworkId = f.get("network") as ObsNetworkId | null | undefined;

                return calcSiteStyle(obsNetworkId);
            },
        });

        const overlayContentElem = hx("div", { className: "ol-feature-overlay" });
        const overlay = new Overlay({
            autoPan: true,
            positioning: "top-center",
            offset: [0, 16],
            element: overlayContentElem,
        });

        const map = new OlMap({
            target: this.#elem,
            layers: [
                gsiStdEnglish({ visible: false }),
                gsiPhotoAndPale({ visible: false }),
                gsiBlank({ visible: false }),
                gsiPale({ visible: true }),
                gsiStd({ visible: false }),
                sourcesLayer,
            ],
            controls,
            overlays: [overlay],
            interactions,
            view: new View({
                center: fromLonLat(constants.search.defaultMapCenter),
                zoom: constants.search.defaultZoom,

                extent: boundingExtent([fromLonLat([180, 80]), fromLonLat([-180, -80])]),
                enableRotation: false,
                constrainResolution: true,
                smoothExtentConstraint: true,
                minZoom: 2,
                maxZoom: 18,
                smoothResolutionConstraint: true,
                multiWorld: false,
            }),
        });

        map.on("click", (e) => {
            const features = map.getFeaturesAtPixel(e.pixel, { layerFilter: (l) => l === sourcesLayer }) as Feature<PointGeom>[];
            if (!features.length) {
                overlay.setPosition(undefined);
                return;
            }

            const nearest = findNearestFeature(e.coordinate, features);
            const siteid2 = nearest.get("siteid2") as string;
            const network = nearest.get("network") as ObsNetworkId;
            const siteCode = nearest.get("siteCode") as string;
            const siteName = nearest.get("siteName") as string;
            const url = pageUrls["/station/[siteid2]"].get({ params: { siteid2 } });

            const content = [
                hx("div",
                    "観測網: ",
                    obsNetworkIdToString(network)),
                hx("div",
                    "観測点コード: ",
                    siteCode),
                hx("div",
                    "観測点名: ",
                    siteName),
                hx("div",
                    hx("a", { href: url, target: "_blank" }, "詳細")),
            ];

            overlayContentElem.replaceChildren(...content);
            overlay.setPosition(nearest.getGeometry()?.getCoordinates());
        });
    }

    /**
     * 観測点データを地図上に表示する。
     *
     * @param sites 表示する観測点データの配列
     */
    setSites(sites: Site[] | undefined) {
        const source = this.#source;
        if (sites?.length) {
            const features = orderBy(sites)
                .map((x) => {
                    const coord4326 = [x.lon, x.lat];
                    const coord3857 = fromLonLat(coord4326);

                    const feature = new Feature(new PointGeom(coord3857));

                    feature.set("siteid2", x.siteid2);
                    feature.set("network", x.obsNetworkId);
                    feature.set("siteCode", x.siteCode);
                    feature.set("siteName", x.siteName);

                    return feature;
                });

            source.clear();
            source.addFeatures(features);
        } else {
            source.clear();
        }
    }
}
